import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import { SITE } from "@/config/site";

const SECTIONS = [
  {
    h: "Who we are",
    p: [
      'This website is operated by DJC Global ("DJC Global," "we," "us"). DJC Global connects businesses with independent solution providers; those providers deliver their own services under their own terms.',
      "[Owner to confirm: legal entity name, state of registration, and jurisdiction of operation.]",
    ],
  },
  {
    h: "What we collect",
    p: [
      "When you submit an inquiry through this site, we collect the information you provide: your name, business email address, company name, optional phone number, the solution you're interested in, and the message describing what you'd like to improve.",
      "We do not collect patient information, financial account details, or sensitive documents through this site, and we ask that you not send them.",
    ],
  },
  {
    h: "How we use it",
    p: [
      "We use inquiry information for one purpose: to review what you shared, identify a relevant solution, and connect you with the appropriate specialist. This may include contacting you by email or phone about your inquiry.",
      "We do not sell your personal information.",
    ],
  },
  {
    h: "When we share it",
    p: [
      "When you ask us to connect you with a solution, we share the relevant details of your inquiry with that specialist provider so they can respond to you. Providers handle your information under their own privacy policies.",
      "We also use a managed email delivery service to process inquiry notifications on our side.",
    ],
  },
  {
    h: "Referral links",
    p: [
      "This site links to independent provider websites. Once you leave our site, your activity is governed by that provider's privacy practices — we encourage you to review them.",
      "DJC Global may receive compensation from certain referrals. See our Referral Disclosure for details.",
    ],
  },
  {
    h: "Analytics",
    p: [
      "This site may use standard analytics tooling to understand aggregate usage — pages visited and general interaction patterns. No inquiry content is used for advertising.",
    ],
  },
  {
    h: "Your choices",
    p: [
      "You may ask us to correct or delete the inquiry information you sent us, or ask that we not follow up. Contact us using the details below and we'll take care of it.",
    ],
  },
  {
    h: "Retention",
    p: [
      "We keep inquiry records only as long as needed to respond to your request and maintain a record of the introduction, unless a longer period is required by law.",
    ],
  },
  {
    h: "Changes to this policy",
    p: [
      "If we update this policy, the revised version will be posted on this page with a new effective date. Material changes will be summarized here.",
    ],
  },
];

export default function Privacy() {
  return (
    <>
      <Seo
        title="Privacy Policy"
        description="How DJC Global collects, uses, and protects the information you share through our contact and referral experience."
        path="/privacy"
      />
      <PageHero
        eyebrow="Privacy Policy"
        title="Plain words about your information."
        sub="We collect the minimum needed to make a good introduction — and nothing sensitive."
      />
      <section className="bg-platinum pb-20 pt-4" data-testid="privacy-body">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="mb-10 flex flex-wrap gap-x-8 gap-y-1 border-b border-silver/60 pb-6 text-sm text-ink/60">
              <span>
                <strong className="font-semibold text-ink">Effective date:</strong> [Owner to confirm]
              </span>
              <span>
                <strong className="font-semibold text-ink">Jurisdiction:</strong> [Owner to confirm]
              </span>
            </div>
          </Reveal>
          <div className="space-y-10">
            {SECTIONS.map((s, i) => (
              <Reveal key={s.h} delay={i * 0.03}>
                <div data-testid={`privacy-section-${i}`}>
                  <h2 className="font-display text-xl font-bold text-ink">{s.h}</h2>
                  {s.p.map((para, j) => (
                    <p key={j} className="mt-3 leading-relaxed text-ink/75">
                      {para}
                    </p>
                  ))}
                </div>
              </Reveal>
            ))}
            <Reveal>
              <div className="rounded-md border border-silver/60 bg-white p-6" data-testid="privacy-contact">
                <h2 className="font-display text-xl font-bold text-ink">Contact about privacy</h2>
                <p className="mt-3 text-ink/75">
                  Questions or requests: email{" "}
                  <a href={`mailto:${SITE.email}`} className="font-semibold text-cobalt hover:text-midnight">
                    {SITE.email}
                  </a>{" "}
                  or call{" "}
                  <a href={SITE.phoneHref} className="font-semibold text-cobalt hover:text-midnight">
                    {SITE.phone}
                  </a>
                  . See also our{" "}
                  <Link to="/referral-disclosure" className="font-semibold text-cobalt hover:text-midnight">
                    Referral Disclosure
                  </Link>
                  .
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
